import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import { Input } from '../components/FormField';
import { AvatarWithFallback } from '../components/Avatar';

function MemberRow({ member, isCreator, teamCreatorId, onConfirm, onReject, onRemove }) {
  const { user } = useAuth();
  const isMe = user && member.user_id === user.id;
  const isTeamCreator = member.user_id === teamCreatorId;

  const statusStyle = {
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    rejected: 'bg-red-50 text-red-500 border-red-200',
  }[member.status] || '';

  const statusLabel = { confirmed: 'Bestätigt', pending: 'Ausstehend', rejected: 'Abgelehnt' }[member.status] || member.status;

  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-3">
        <AvatarWithFallback user={member.user} size="sm" />
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/users/${member.user_id}`} className="font-medium text-slate-800 hover:text-ocean-600 hover:underline">
              {member.user?.nickname || member.user?.username}
            </Link>
            {isTeamCreator && (
              <span className="text-[10px] bg-ocean-100 text-ocean-700 px-1.5 py-0.5 rounded font-bold uppercase">Captain</span>
            )}
            {isMe && <span className="text-[10px] text-slate-400">(Du)</span>}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${statusStyle}`}>
              {statusLabel}
            </span>
            {member.seat_label && (
              <span className="text-[10px] bg-sand-100 text-sand-700 border border-sand-200 px-1.5 py-0.5 rounded font-bold">
                Platz {member.seat_label}
              </span>
            )}
          </div>
        </div>
      </div>
      {isCreator && !isTeamCreator && (
        <div className="flex gap-1.5">
          {member.status === 'pending' && (
            <>
              <Button size="sm" variant="success" onClick={() => onConfirm(member.user_id)}>✓</Button>
              <Button size="sm" variant="danger" onClick={() => onReject(member.user_id)}>✗</Button>
            </>
          )}
          {member.status === 'confirmed' && (
            <Button size="sm" variant="danger" onClick={() => onRemove(member.user_id)}>Entfernen</Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function TeamDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [inviteSearch, setInviteSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    api.get(`/api/teams/${id}`)
      .then((r) => {
        setTeam(r.data);
        return api.get(`/api/tournaments/${r.data.tournament_id}`);
      })
      .then((r) => setTournament(r.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const isCreator = user && team && team.creator_id === user.id;
  const isMember = user && team && (team.members || []).some(
    (m) => m.user_id === user.id && m.status === 'confirmed'
  );

  const act = async (fn) => {
    setError(''); setMessage('');
    try { await fn(); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  const confirm = (uid) => act(() => api.post(`/api/teams/${id}/members/${uid}/confirm`));
  const reject = (uid) => act(() => api.post(`/api/teams/${id}/members/${uid}/reject`));
  const remove = (uid) => act(() => api.delete(`/api/teams/${id}/members/${uid}`));

  const leave = async () => {
    if (!window.confirm('Team wirklich verlassen?')) return;
    setLeaving(true);
    try {
      await api.post(`/api/teams/${id}/leave`);
      navigate(`/tournaments/${team.tournament_id}`);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setLeaving(false); }
  };

  const deleteTeam = async () => {
    if (!window.confirm('Team wirklich auflösen?')) return;
    setDeleting(true);
    try {
      await api.delete(`/api/teams/${id}`);
      navigate(`/tournaments/${team.tournament_id}`);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setDeleting(false); }
  };

  const searchUsers = async () => {
    if (inviteSearch.trim().length < 2) return;
    try {
      const res = await api.get(`/api/users/search?q=${encodeURIComponent(inviteSearch)}`);
      setSearchResults(res.data);
    } catch { setSearchResults([]); }
  };

  const invite = async (userId) => {
    setInviting(true); setError('');
    try {
      await api.post(`/api/teams/${id}/invite/${userId}`);
      setMessage('Einladung gesendet!');
      setSearchResults([]);
      setInviteSearch('');
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setInviting(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" />
    </div>
  );

  if (!team) return null;

  const confirmed = (team.members || []).filter((m) => m.status === 'confirmed');
  const pending = (team.members || []).filter((m) => m.status === 'pending');
  const isFull = team.is_full;
  const teamSize = tournament?.team_size || 1;
  const isAdminAssigned = tournament?.team_assignment_mode === 'admin_assigned';

  const pendingCount = pending.length;

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      {/* Back */}
      {tournament && (
        <Link to={`/tournaments/${team.tournament_id}`}
          className="text-sm text-ocean-600 hover:underline mb-4 inline-block">
          ← {tournament.name}
        </Link>
      )}

      {/* Team Header */}
      <div className="card mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-ocean-900">{team.name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${
                isFull ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                'bg-slate-50 text-slate-500 border-slate-200'
              }`}>
                {confirmed.length}/{teamSize} Spieler{isFull ? ' – Bereit' : ''}
              </span>
              {pendingCount > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 font-semibold">
                  {pendingCount} ausstehend
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {isAdminAssigned && (
              <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-2 py-1 rounded border border-amber-200 self-center">
                Team wird vom Admin verwaltet
              </span>
            )}
            {!isAdminAssigned && isMember && !isCreator && (
              <Button variant="danger" size="sm" onClick={leave} loading={leaving}>Team verlassen</Button>
            )}
            {!isAdminAssigned && isCreator && (
              <Button variant="danger" size="sm" onClick={deleteTeam} loading={deleting}>Team auflösen</Button>
            )}
          </div>
        </div>
      </div>

      {message && <div className="alert-success mb-4">{message}</div>}
      {error && <div className="alert-error mb-4">{error}</div>}

      {/* Members */}
      <div className="card mb-6">
        <h2 className="section-title mb-3">Mitglieder</h2>
        {(team.members || []).length === 0 ? (
          <p className="text-sm text-slate-400">Keine Mitglieder.</p>
        ) : (
          <div>
            {[...confirmed, ...pending].map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                isCreator={isCreator && !isAdminAssigned}
                teamCreatorId={team.creator_id}
                onConfirm={confirm}
                onReject={reject}
                onRemove={remove}
              />
            ))}
            {/* Empty slots */}
            {confirmed.length < teamSize && Array.from({ length: teamSize - confirmed.length }).map((_, i) => (
              <div key={i} className="py-3 border-b border-dashed border-slate-100 last:border-b-0 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300 text-sm flex-shrink-0">
                  ?
                </div>
                <span className="text-sm text-slate-300 italic">Freier Platz</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite (only creator, team not full, self_organized only) */}
      {!isAdminAssigned && isCreator && !isFull && !tournament?.bracket_generated && (
        <div className="card">
          <h2 className="section-title mb-3">Spieler einladen</h2>
          <div className="flex gap-2 mb-3">
            <Input
              value={inviteSearch}
              onChange={(e) => setInviteSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
              placeholder="Username suchen..."
              className="flex-1"
            />
            <Button size="sm" onClick={searchUsers}>Suchen</Button>
          </div>
          {searchResults.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {searchResults.map((u) => (
                <div key={u.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
                  <div className="flex items-center gap-2">
                    <AvatarWithFallback user={u} size="sm" />
                    <div>
                      <span className="font-medium text-slate-800">{u.username}</span>
                      {u.nickname && <span className="text-slate-400 text-sm ml-2">({u.nickname})</span>}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => invite(u.id)} loading={inviting}>Einladen</Button>
                </div>
              ))}
            </div>
          )}
          {searchResults.length === 0 && inviteSearch.length >= 2 && (
            <p className="text-sm text-slate-400">Keine Benutzer gefunden. Suche und drücke Enter.</p>
          )}
        </div>
      )}
    </div>
  );
}
